import { Router } from 'aurelia-router';
import { bindable, computedFrom, autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import * as moment from 'moment';
import numeral from 'numeral';
import { PayrazrService } from '../../services/payrazr';
import { State } from '../../services/global-state';
import { Offer } from '../../models/offer';
import { HpfCustomerService, MakeOfferResponse } from '../../services/hpf-customer.service';
import { Account } from '../../models/customer';
import { Installment } from '../../models/installment';
import { LockedFields } from '../installments/setup-installment';
import { PaymentItemType } from '../../smart/models/enums/payment-item-type';
import { PaymentItemTableConfiguration } from '../../smart/models/payment-item-table-configuration';

@autoinject
export class Negotiator {
	isSubmitting: boolean = false;
	validationErrors: string[];
	offerSubmitted: boolean = false;
	offerAccepted: boolean = false;
	selectedFrequency: number;
	offerAmount: string;
	acceptedOffer: Offer;
	counterOffer: Offer;
	negotiatorActivitySessionId: string;
	installmentSettings;
	portfolioSettings;
	account: Account;
	paymentItemTableConfiguration: PaymentItemTableConfiguration;
	customerAccountKeys: number[];
	businessKey;
	balanceRange;

	constructor(private router: Router, private globalState: State, private payrazrService: PayrazrService, private hpfCustomerService: HpfCustomerService, private i18n: I18N) {
	}

	activate(params, routeData) {

		this.account = this.globalState.accounts.find((element, index, array) => {
			return element.id === params.id;
		});

		this.businessKey = this.globalState.business.key;
		this.portfolioSettings = this.globalState.business.fetchPortfolioSettings(this.account.portfolioName).settings;
		this.installmentSettings = this.portfolioSettings.paymentTypes.installments;

		this.paymentItemTableConfiguration = new PaymentItemTableConfiguration();
		this.paymentItemTableConfiguration.itemType = PaymentItemType.Account;
		this.paymentItemTableConfiguration.selectFirstItemUponLoad = false;
		this.paymentItemTableConfiguration.showBucket = true;
		this.paymentItemTableConfiguration.onlyShowSelected = true;
		this.paymentItemTableConfiguration.enableItemSelection = false;
		this.paymentItemTableConfiguration.useMultiselect = false;

		this.customerAccountKeys = (this.globalState.accounts || []).filter(item => {
			let portfolio = this.globalState.business.fetchPortfolioSettings(item.portfolioName);
			if (!portfolio || !portfolio.hasSettings || !portfolio.settings.paymentMethods) {
				return false;
			}
			return true;
		}).map(x => x.customerAccountKey);

		this.installmentSettings.balanceRanges.some((range) => {
			if (this.account.balance >= range.min && this.account.balance <= range.max) {
				this.balanceRange = range;
				return true; // This breaks out of the loop taking the first range in the list 
			}
		});
	}

	translateFrequency(frequencyId: number) {
		switch (frequencyId) {
			case 0: // should be caught in validation, may need testing
				return this.i18n.i18next.t('selectAValue', 'Please select a value');
			case 1:
				return this.i18n.i18next.t('weekly', 'Weekly');
			case 2:
				return this.i18n.i18next.t('every2Weeks', 'Every 2 weeks');
			case 3:
				return this.i18n.i18next.t('every28Days', 'Every 28 Days');
			case 4:
				return this.i18n.i18next.t('monthly', 'Monthly');
			case 5:
				return this.i18n.i18next.t('eachMonth15', '15th of each month');
			case 6:
				return this.i18n.i18next.t('endOfMonth', 'End of month');
			case 7:
				return this.i18n.i18next.t('every2Months', 'Every 2 months');
		}
	}

	@computedFrom('balanceRange')
	get availableFrequencyIds(): number[] {
		let opts: number[] = this.balanceRange.recurringPaymentsFrequencies.map(freq => freq.id);
		return [0, ...opts]; // prepend 'Please select a value' option
	}

	get availableFrequencyDescriptions(): string[] {
		return this.availableFrequencyIds.map(id => this.translateFrequency(id));
	}

	formatEnteredAmount() {
		this.offerAmount = numeral(this.offerAmount).format('(0.00)');
	}

	submitOffer() {
		this.isSubmitting = true;
		this.validate();
		if (this.validationErrors.length === 0) {

			this.hpfCustomerService.makeOffer(this.account.customerAccountKey, this.selectedFrequency, this.offerAmount).then(
				(result: MakeOfferResponse) => {
					if (!result) {
						this.validationErrors = [this.i18n.i18next.t('anErrorOccurredProcessingOffer', 'An error occured while processing your offer')];
						this.isSubmitting = false;
						return;
					}

					this.offerSubmitted = true;
					this.offerAccepted = result.accepted;
					this.counterOffer = result.counterOffer;
					this.acceptedOffer = result.acceptedOffer;
					this.negotiatorActivitySessionId = result.negotiatorActivitySessionId;
					this.isSubmitting = false;
				},
				(error) => {
					console.error(error);
					this.validationErrors = [this.i18n.i18next.t('anErrorOccurredProcessingOffer', 'An error occured while processing your offer')];
					this.isSubmitting = false;
				}
			);
		} else {
			this.isSubmitting = false;
		}
	}

	validate() {
		this.validationErrors = [];

		if (!this.offerAmount || parseInt(this.offerAmount, 10) <= 0) {
			this.validationErrors.push(this.i18n.i18next.t('selectPaymentAmount', 'Please select a payment amount'));
		}

		if (!this.selectedFrequency) {
			this.validationErrors.push(this.i18n.i18next.t('selectFrequencyOfRecurringPayments', 'Please select the frequency of recurring payments'));
		}

		if (this.validationErrors) {
			setTimeout(() => { // wait until it loads to focus
				const validationSummary = document.getElementsByTagName('validation-summary')[0] as HTMLElement;
				validationSummary?.focus();
			}, 300);
		}
	}

	selectOffer(offer: Offer, offerType: number) {
		let selectedFrequency = offer.frequency;

		let lockedFields: LockedFields = new LockedFields();
		lockedFields.amount = true;
		lockedFields.frequency = true;

		let plan: Installment = new Installment();
		plan.discountPercent = offer.discount ? 0 : offer.discount;
		plan.totalAmount = offer.totalPaid.toFixed(2);
		plan.paymentAfterFirst = offer.amount;
		plan.number = offer.numberOfPayments;

		this.logSelectOffer(offerType);

		const setupPlanRoute = this.router.routes.find(x => x.name === 'installments/setup-payment-plan');
		setupPlanRoute.installment = plan;
		setupPlanRoute.negotiatorActivitySessionId = this.negotiatorActivitySessionId;
		setupPlanRoute.overrides = {
			selectedFrequency,
			lockedFields,
		};
		setupPlanRoute.accounts = [this.account];
		this.router.navigateToRoute(setupPlanRoute.name, { id: this.account.id });
	}

	logSelectOffer(offerType: number) {
		this.hpfCustomerService.logOfferSelected(this.negotiatorActivitySessionId, offerType).catch(
			(error) => {
				console.error('Error logging offer selected');
			});
	}

	requestContact() {
		let route = this.router.routes.find(r => r.name === 'contactMe');
		route.accountId = this.account.id;
		this.router.navigateToRoute('contactMe');
	}

	// This is an account summary event. It's needed to prevent console errors
	paymentItemsLoaded() {	}
}
